/**
 * localStorageCrud: classPlannerData (학생/과목/강사/세션/등록) 의 localStorage SSOT
 * CRUD + cross-entity cascade reconcile 만 담당.
 *
 * 의존성:
 *   - storage: window.localStorage (SSR 안전 — typeof window 가드)
 *   - 동기화: storage event → in-module cache invalidate
 *   - 호출 흐름: useXxxLocal hooks → 이 파일 → window.localStorage
 *   - non-goal: server API 호출 (apiSync.ts 책임)
 *
 * 결정 history:
 *   - ADR-013: anonymous → 로그인 마이그레이션 entity 누락 가드
 *   - ADR-002 (2026-05-27): Cohesion Sweep — sub-domain 분리 검토 후 보류
 *     (cross-entity cascade invariant 가 강해 같이 두는 게 자연스러움)
 *   - UAT 2026-05-09: deferred-commit + await — race window 0 (ADR-012)
 *   - UAT 2026-05-10: 중복 검사 정책 (이름+성별+생년월일 / 이름+이메일+전화)
 *   - 2026-05-27 admin 첫 로그인: setActiveAcademyId setTimeout 0 microtask
 *     (same-tab localStorage.setItem 의 storage event 발화 X 가드)
 */

import { logger } from "./logger";
import { migrateLocalSessionsIfNeeded } from "./migrateLocalSessions";
import type { Enrollment, Session, Student, Subject, Teacher, TeacherRole } from "./planner";

// ===== 타입 정의 =====

export interface ClassPlannerData {
  students: Student[];
  subjects: Subject[];
  sessions: Session[];
  enrollments: Enrollment[];
  teachers: Teacher[];
  version: string;
  lastModified: string;
}

export interface CrudResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// ===== 상수 =====

export const ANONYMOUS_STORAGE_KEY = "classPlannerData:anonymous";

// Active academy management
const ACTIVE_ACADEMY_KEY_PREFIX = "active_academy";

export function getActiveAcademyId(userId: string): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(`${ACTIVE_ACADEMY_KEY_PREFIX}:${userId}`);
}

export function setActiveAcademyId(userId: string, academyId: string): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(`${ACTIVE_ACADEMY_KEY_PREFIX}:${userId}`, academyId);
  // Same-tab race window 회피 (2026-05-27 admin 첫 로그인 사고):
  // localStorage.setItem 은 same-tab storage event 발화 X. useIntegratedDataLocal /
  // useGlobalDataInitialization 등 listener 들이 active_academy 변화 인지 못해 stale
  // legacy fallback key data 유지 → 사용자 화면에 "수업 없음" 노출 (새로고침으로만 회복).
  // setTimeout 0 으로 microtask 분리 — test 의 sync expect 영향 X, 사용자 perceive X (ms 단위).
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent("class-planner:academy-changed"));
    window.dispatchEvent(new CustomEvent("classPlannerDataChanged"));
  }, 0);
}

export function clearActiveAcademy(userId: string): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(`${ACTIVE_ACADEMY_KEY_PREFIX}:${userId}`);
  // Same-tab race 회피 (위 setActiveAcademyId 와 동일 이유, setTimeout 0 microtask 분리)
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent("class-planner:academy-changed"));
    window.dispatchEvent(new CustomEvent("classPlannerDataChanged"));
  }, 0);
}

export function getStorageKey(academyId?: string): string {
  if (typeof window === "undefined") return ANONYMOUS_STORAGE_KEY;
  const userId = localStorage.getItem("supabase_user_id");
  if (!userId) return ANONYMOUS_STORAGE_KEY;

  const activeAcademyId = academyId ?? getActiveAcademyId(userId);
  if (activeAcademyId) {
    return `classPlannerData:${userId}:${activeAcademyId}`;
  }
  // Legacy fallback (single-academy era — still works)
  return `classPlannerData:${userId}`;
}

// ===== In-module memoization cache =====
// classPlannerData는 51 호출처에서 매번 localStorage.getItem + JSON.parse(K바이트).
// 같은 mount 동안 set/clear 시점에서만 invalidate되도록 module-level Map 캐시.
// 다른 tab의 변경은 window 'storage' event로 감지하여 invalidate.
//
// Cache key는 storage key 그대로 (academy/anonymous 자동 분리).
const dataCache = new Map<string, ClassPlannerData>();

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    // 다른 tab이 같은 key를 변경하면 cache invalidate (다음 read에서 fresh load)
    if (e.key && e.key.startsWith("classPlannerData:")) {
      dataCache.delete(e.key);
    }
  });
}

/**
 * Test-only: in-module cache 전체 비우기.
 * 단위 테스트의 beforeEach에서 호출. production 코드에서 사용 금지.
 */
export function __resetCacheForTest(): void {
  dataCache.clear();
}

function migrateUnkeyedStorage(): void {
  if (typeof window === "undefined") return;
  const legacy = localStorage.getItem("classPlannerData");
  if (!legacy) return;
  const currentKey = getStorageKey();
  if (!localStorage.getItem(currentKey)) {
    localStorage.setItem(currentKey, legacy);
  }
  localStorage.removeItem("classPlannerData");
}
const createDefaultData = (): ClassPlannerData => ({
  students: [],
  subjects: [],
  sessions: [],
  enrollments: [],
  teachers: [],
  version: "1.0",
  lastModified: new Date().toISOString(),
});

// ===== 기본 CRUD 함수들 =====

/**
 * localStorage에서 classPlannerData 안전하게 읽기
 */
export const getClassPlannerData = (academyId?: string): ClassPlannerData => {
  try {
    if (typeof window === "undefined") {
      logger.debug("localStorageCrud - SSR 환경, 기본 데이터 반환");
      return createDefaultData();
    }

    // Cache hit short-circuit — set/clear/storage-event 외엔 invalidate 없음.
    const cacheKey = getStorageKey(academyId);
    const cached = dataCache.get(cacheKey);
    if (cached) return cached;

    migrateUnkeyedStorage();

    const newKey = getStorageKey(academyId);
    const userId = localStorage.getItem("supabase_user_id");

    // One-time migration: if new scoped key is empty but legacy key has data, copy it
    if (userId && academyId) {
      const legacyKey = `classPlannerData:${userId}`;
      const hasNewData = !!localStorage.getItem(newKey);
      if (!hasNewData) {
        const legacyRaw = localStorage.getItem(legacyKey);
        if (legacyRaw) {
          localStorage.setItem(newKey, legacyRaw);
          // Keep legacy key — don't delete (safe rollback)
          logger.info("localStorageCrud - academy 스코프 마이그레이션 실행", {
            userId,
            academyId,
            legacyKey,
            newKey,
          });
        }
      }
    }

    const stored = localStorage.getItem(newKey);
    if (!stored) {
      logger.debug("localStorageCrud - 저장된 데이터 없음, 기본 데이터 반환");
      return createDefaultData();
    }

    const parsed = JSON.parse(stored);

    // 데이터 구조 검증
    if (!parsed || typeof parsed !== "object") {
      logger.warn("localStorageCrud - 잘못된 데이터 구조, 기본 데이터 반환");
      return createDefaultData();
    }

    // 기본 구조 확인 및 마이그레이션
    const migrated = migrateLocalSessionsIfNeeded(parsed);
    const result: ClassPlannerData = {
      students: migrated.students || [],
      subjects: migrated.subjects || [],
      sessions: migrated.sessions || [],
      enrollments: migrated.enrollments || [],
      teachers: migrated.teachers || [],
      version: migrated.version || "1.0",
      lastModified: migrated.lastModified || new Date().toISOString(),
    };

    // lastModified가 없으면 추가하고 저장
    if (!parsed.lastModified) {
      logger.info("localStorageCrud - lastModified 마이그레이션 실행");
      setClassPlannerData(result, academyId);
    }

    logger.debug("localStorageCrud - 데이터 로드 성공", {
      studentCount: result.students.length,
      subjectCount: result.subjects.length,
      sessionCount: result.sessions.length,
      enrollmentCount: result.enrollments.length,
    });

    // Cache populate — 다음 호출은 short-circuit (set/clear 시 invalidate)
    dataCache.set(cacheKey, result);
    return result;
  } catch (error) {
    logger.error(
      "localStorageCrud - 데이터 읽기 실패:",
      undefined,
      error as Error
    );
    return createDefaultData();
  }
};

/**
 * localStorage에 classPlannerData 안전하게 저장
 */
export const setClassPlannerData = (data: ClassPlannerData, academyId?: string): boolean => {
  try {
    if (typeof window === "undefined") {
      logger.debug("localStorageCrud - SSR 환경, 저장 건너뜀");
      return false;
    }

    // 데이터 저장 준비.
    // sub-array도 새 reference로 만들어 cache에 저장한다. dataCache(2bad68f)가
    // 같은 reference를 반환하기 때문에, 호출자가 push/splice로 mutate한 array가
    // cache에 그대로 남으면 다음 setData(localData) 시 React.memo가 sub-array
    // reference 동일로 판정 → DOM 미갱신 회귀 (T8 e2e 사고, ADR-013).
    // 1-level shallow copy면 충분 — sub-array 안의 entity 객체 reference는 보존
    // (immutable 사용 가정). entity 자체 mutate는 별개 책임.
    const dataToSave: ClassPlannerData = {
      ...data,
      students: [...data.students],
      subjects: [...data.subjects],
      sessions: [...data.sessions],
      enrollments: [...data.enrollments],
      teachers: [...data.teachers],
    };

    const writeKey = getStorageKey(academyId);
    localStorage.setItem(writeKey, JSON.stringify(dataToSave));
    // Cache update — 다음 read는 cache에서 (다른 tab은 storage event로 자체 invalidate)
    dataCache.set(writeKey, dataToSave);

    logger.debug("localStorageCrud - 데이터 저장 성공", {
      studentCount: dataToSave.students.length,
      subjectCount: dataToSave.subjects.length,
      sessionCount: dataToSave.sessions.length,
      enrollmentCount: dataToSave.enrollments.length,
    });

    // localStorage 변경 이벤트 발생 (다른 탭 동기화)
    try {
      window.dispatchEvent(
        new CustomEvent("classPlannerDataChanged", {
          detail: dataToSave,
        })
      );
    } catch (eventError) {
      // 테스트/비표준 환경에서 CustomEvent 미지원 등으로 실패해도 저장은 성공 처리
      logger.warn("localStorageCrud - 변경 이벤트 디스패치 실패 (무시)");
    }

    return true;
  } catch (error) {
    logger.error(
      "localStorageCrud - 데이터 저장 실패:",
      undefined,
      error as Error
    );
    return false;
  }
};

/**
 * localStorage 데이터 초기화
 */
export const clearClassPlannerData = (academyId?: string): boolean => {
  try {
    if (typeof window === "undefined") {
      return false;
    }

    const clearKey = getStorageKey(academyId);
    localStorage.removeItem(clearKey);
    dataCache.delete(clearKey);
    logger.info("localStorageCrud - 데이터 초기화 완료");

    // 초기화 이벤트 발생 (실패해도 무시)
    try {
      window.dispatchEvent(new CustomEvent("classPlannerDataCleared"));
    } catch (eventError) {
      logger.warn("localStorageCrud - 초기화 이벤트 디스패치 실패 (무시)");
    }

    return true;
  } catch (error) {
    logger.error(
      "localStorageCrud - 데이터 초기화 실패:",
      undefined,
      error as Error
    );
    return false;
  }
};

// ===== Students CRUD =====

/**
 * 학생 추가
 */
export const addStudentToLocal = (
  name: string,
  options?: { gender?: string; birthDate?: string; grade?: string; school?: string; phone?: string }
): CrudResult<Student> => {
  try {
    const data = getClassPlannerData();

    // 새 학생 생성
    const newStudent: Student = {
      id: crypto.randomUUID(),
      name: name.trim(),
      ...(options?.gender !== undefined && { gender: options.gender }),
      ...(options?.birthDate !== undefined && { birthDate: options.birthDate }),
      ...(options?.grade !== undefined && { grade: options.grade }),
      ...(options?.school !== undefined && { school: options.school }),
      ...(options?.phone !== undefined && { phone: options.phone }),
    };

    // 중복 검사 (UAT 2026-05-10): 이름+성별+생년월일 모두 일치 시에만 동일인.
    // 한 필드라도 다르면 동명이인 → 새 학생 등록 허용. server idempotent와 일관.
    const norm = (v: string | null | undefined) => (v ?? "").trim();
    const isDuplicate = data.students.some(
      (s) =>
        norm(s.name).toLowerCase() === norm(newStudent.name).toLowerCase() &&
        norm(s.gender) === norm(newStudent.gender) &&
        norm(s.birthDate) === norm(newStudent.birthDate),
    );
    if (isDuplicate) {
      return {
        success: false,
        error: "이미 동일한 학생(이름·성별·생년월일 일치)이 존재합니다.",
      };
    }

    // 데이터 업데이트
    data.students.push(newStudent);
    data.lastModified = new Date().toISOString(); // CRU 작업 시 lastModified 갱신

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 학생 추가 성공", {
        studentId: newStudent.id,
        name: newStudent.name,
      });

      return {
        success: true,
        data: newStudent,
      };
    } else {
      return {
        success: false,
        error: "localStorage 저장 실패",
      };
    }
  } catch (error) {
    logger.error(
      "localStorageCrud - 학생 추가 실패:",
      undefined,
      error as Error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "학생 추가 실패",
    };
  }
};

/**
 * 학생 수정
 */
export const updateStudentInLocal = (
  id: string,
  updates: { name?: string; gender?: string; birthDate?: string; grade?: string; school?: string; phone?: string }
): CrudResult<Student> => {
  try {
    const data = getClassPlannerData();
    const studentIndex = data.students.findIndex((s) => s.id === id);

    if (studentIndex === -1) {
      return {
        success: false,
        error: "학생을 찾을 수 없습니다.",
      };
    }

    // 중복 검사 — 변경 후 식별 조합이 다른 학생과 충돌하는지 (UAT 2026-05-10).
    {
      const current = data.students[studentIndex];
      const merged = {
        name: updates.name !== undefined ? updates.name.trim() : current.name,
        gender: updates.gender !== undefined ? updates.gender : current.gender,
        birthDate:
          updates.birthDate !== undefined ? updates.birthDate : current.birthDate,
      };
      const norm = (v: string | null | undefined) => (v ?? "").trim();
      const isDuplicate = data.students.some(
        (s, index) =>
          index !== studentIndex &&
          norm(s.name).toLowerCase() === norm(merged.name).toLowerCase() &&
          norm(s.gender) === norm(merged.gender) &&
          norm(s.birthDate) === norm(merged.birthDate),
      );
      if (isDuplicate) {
        return {
          success: false,
          error: "이미 동일한 학생(이름·성별·생년월일 일치)이 존재합니다.",
        };
      }
    }

    // 학생 업데이트
    const updatedStudent: Student = {
      ...data.students[studentIndex],
      ...(updates.name && { name: updates.name.trim() }),
      ...(updates.gender !== undefined && { gender: updates.gender }),
      ...(updates.birthDate !== undefined && { birthDate: updates.birthDate }),
      ...(updates.grade !== undefined && { grade: updates.grade }),
      ...(updates.school !== undefined && { school: updates.school }),
      ...(updates.phone !== undefined && { phone: updates.phone }),
    };

    data.students[studentIndex] = updatedStudent;
    data.lastModified = new Date().toISOString(); // CRU 작업 시 lastModified 갱신

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 학생 수정 성공", {
        studentId: id,
        updates,
      });

      return {
        success: true,
        data: updatedStudent,
      };
    } else {
      return {
        success: false,
        error: "localStorage 저장 실패",
      };
    }
  } catch (error) {
    logger.error(
      "localStorageCrud - 학생 수정 실패:",
      undefined,
      error as Error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "학생 수정 실패",
    };
  }
};

/**
 * 학생 삭제
 */
export const deleteStudentFromLocal = (id: string): CrudResult<boolean> => {
  try {
    const data = getClassPlannerData();
    const studentIndex = data.students.findIndex((s) => s.id === id);

    if (studentIndex === -1) {
      return {
        success: false,
        error: "학생을 찾을 수 없습니다.",
      };
    }

    // 관련 enrollments 및 sessions 정리
    const deletedStudent = data.students[studentIndex];
    data.students.splice(studentIndex, 1);

    // 1) 해당 학생의 enrollments 수집 및 삭제
    const targetEnrollments = data.enrollments.filter(
      (e) => e.studentId === id
    );
    const targetEnrollmentIds = new Set(targetEnrollments.map((e) => e.id));
    data.enrollments = data.enrollments.filter((e) => e.studentId !== id);

    // 2) 세션에서 해당 enrollmentIds 제거, 비어 있으면 세션 삭제
    data.sessions = data.sessions
      .map((session) => {
        if (session.enrollmentIds && session.enrollmentIds.length > 0) {
          const filtered = session.enrollmentIds.filter(
            (eId) => !targetEnrollmentIds.has(eId)
          );
          if (filtered.length !== session.enrollmentIds.length) {
            return { ...session, enrollmentIds: filtered };
          }
        }
        return session;
      })
      .filter(
        (session) => session.enrollmentIds && session.enrollmentIds.length > 0
      );
    data.lastModified = new Date().toISOString(); // CRU 작업 시 lastModified 갱신

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 학생 삭제 성공", {
        studentId: id,
        name: deletedStudent.name,
        deletedEnrollments: data.enrollments.length,
      });

      return {
        success: true,
        data: true,
      };
    } else {
      return {
        success: false,
        error: "localStorage 저장 실패",
      };
    }
  } catch (error) {
    logger.error(
      "localStorageCrud - 학생 삭제 실패:",
      undefined,
      error as Error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "학생 삭제 실패",
    };
  }
};

/**
 * 학생 조회
 */
export const getStudentFromLocal = (id: string): Student | null => {
  try {
    const data = getClassPlannerData();
    return data.students.find((s) => s.id === id) || null;
  } catch (error) {
    logger.error(
      "localStorageCrud - 학생 조회 실패:",
      undefined,
      error as Error
    );
    return null;
  }
};

/**
 * 모든 학생 조회
 */
export const getAllStudentsFromLocal = (): Student[] => {
  try {
    const data = getClassPlannerData();
    return data.students;
  } catch (error) {
    logger.error(
      "localStorageCrud - 학생 목록 조회 실패:",
      undefined,
      error as Error
    );
    return [];
  }
};

// ===== Subjects CRUD =====

/**
 * 과목 추가
 */
export const addSubjectToLocal = (
  name: string,
  color: string
): CrudResult<Subject> => {
  try {
    const data = getClassPlannerData();

    // 새 과목 생성
    const newSubject: Subject = {
      id: crypto.randomUUID(),
      name: name.trim(),
      color: color,
    };

    // 중복 검사 (UAT 2026-05-10): lowercase trim 일치 (server 정책 일관).
    const isDuplicate = data.subjects.some(
      (s) => s.name.trim().toLowerCase() === newSubject.name.trim().toLowerCase(),
    );
    if (isDuplicate) {
      return {
        success: false,
        error: "이미 같은 이름의 과목이 존재합니다.",
      };
    }

    // 데이터 업데이트
    data.subjects.push(newSubject);
    data.lastModified = new Date().toISOString(); // CRU 작업 시 lastModified 갱신

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 과목 추가 성공", {
        subjectId: newSubject.id,
        name: newSubject.name,
        color: newSubject.color,
      });

      return {
        success: true,
        data: newSubject,
      };
    } else {
      return {
        success: false,
        error: "localStorage 저장 실패",
      };
    }
  } catch (error) {
    logger.error(
      "localStorageCrud - 과목 추가 실패:",
      undefined,
      error as Error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "과목 추가 실패",
    };
  }
};

/**
 * 과목 수정
 */
export const updateSubjectInLocal = (
  id: string,
  updates: { name?: string; color?: string }
): CrudResult<Subject> => {
  try {
    const data = getClassPlannerData();
    const subjectIndex = data.subjects.findIndex((s) => s.id === id);

    if (subjectIndex === -1) {
      return {
        success: false,
        error: "과목을 찾을 수 없습니다.",
      };
    }

    // 중복 검사 (자기 자신 제외) — lowercase trim 일치
    if (updates.name) {
      const target = updates.name.trim().toLowerCase();
      const isDuplicate = data.subjects.some(
        (s, index) =>
          index !== subjectIndex && s.name.trim().toLowerCase() === target,
      );
      if (isDuplicate) {
        return {
          success: false,
          error: "이미 같은 이름의 과목이 존재합니다.",
        };
      }
    }

    // 과목 업데이트
    const updatedSubject: Subject = {
      ...data.subjects[subjectIndex],
      ...(updates.name && { name: updates.name.trim() }),
      ...(updates.color && { color: updates.color }),
    };

    data.subjects[subjectIndex] = updatedSubject;
    data.lastModified = new Date().toISOString(); // CRU 작업 시 lastModified 갱신

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 과목 수정 성공", {
        subjectId: id,
        updates,
      });

      return {
        success: true,
        data: updatedSubject,
      };
    } else {
      return {
        success: false,
        error: "localStorage 저장 실패",
      };
    }
  } catch (error) {
    logger.error(
      "localStorageCrud - 과목 수정 실패:",
      undefined,
      error as Error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "과목 수정 실패",
    };
  }
};

/**
 * 과목 삭제
 */
export const deleteSubjectFromLocal = (id: string): CrudResult<boolean> => {
  try {
    const data = getClassPlannerData();
    const subjectIndex = data.subjects.findIndex((s) => s.id === id);

    if (subjectIndex === -1) {
      return {
        success: false,
        error: "과목을 찾을 수 없습니다.",
      };
    }

    // 관련 데이터도 함께 삭제
    const deletedSubject = data.subjects[subjectIndex];
    data.subjects.splice(subjectIndex, 1);

    // 해당 과목의 모든 enrollment 삭제
    const deletedEnrollments = data.enrollments.filter(
      (e) => e.subjectId === id
    );
    data.enrollments = data.enrollments.filter((e) => e.subjectId !== id);

    // 해당 과목의 모든 session 삭제 (enrollmentIds 기반)
    const deletedEnrollmentIds = deletedEnrollments.map((e) => e.id);
    data.sessions = data.sessions.filter(
      (s) => !s.enrollmentIds?.some((eId) => deletedEnrollmentIds.includes(eId))
    );
    data.lastModified = new Date().toISOString(); // CRU 작업 시 lastModified 갱신

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 과목 삭제 성공", {
        subjectId: id,
        name: deletedSubject.name,
        deletedEnrollments: deletedEnrollments.length,
        deletedSessions: data.sessions.length,
      });

      return {
        success: true,
        data: true,
      };
    } else {
      return {
        success: false,
        error: "localStorage 저장 실패",
      };
    }
  } catch (error) {
    logger.error(
      "localStorageCrud - 과목 삭제 실패:",
      undefined,
      error as Error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "과목 삭제 실패",
    };
  }
};

/**
 * 과목 조회
 */
export const getSubjectFromLocal = (id: string): Subject | null => {
  try {
    const data = getClassPlannerData();
    return data.subjects.find((s) => s.id === id) || null;
  } catch (error) {
    logger.error(
      "localStorageCrud - 과목 조회 실패:",
      undefined,
      error as Error
    );
    return null;
  }
};

/**
 * 모든 과목 조회
 */
export const getAllSubjectsFromLocal = (): Subject[] => {
  try {
    const data = getClassPlannerData();
    return data.subjects;
  } catch (error) {
    logger.error(
      "localStorageCrud - 과목 목록 조회 실패:",
      undefined,
      error as Error
    );
    return [];
  }
};

// ===== Teachers CRUD =====

/**
 * 강사 추가
 */
export const addTeacherToLocal = (
  name: string,
  color: string,
  userId?: string | null,
  profile?: {
    email?: string | null;
    phone?: string | null;
    role?: TeacherRole | null;
    notes?: string | null;
    subjectIds?: string[];
  }
): CrudResult<Teacher> => {
  try {
    const data = getClassPlannerData();

    // 중복 검사 (UAT 2026-05-10): 이름+이메일+전화 모두 일치 시에만 동일인.
    const normT = (v: string | null | undefined) => (v ?? "").trim();
    const isDuplicate = data.teachers.some(
      (t) =>
        normT(t.name).toLowerCase() === normT(name).toLowerCase() &&
        normT(t.email) === normT(profile?.email) &&
        normT(t.phone) === normT(profile?.phone),
    );
    if (isDuplicate) {
      return {
        success: false,
        error: "이미 동일한 강사(이름·이메일·전화 일치)가 존재합니다.",
      };
    }

    const newTeacher: Teacher = {
      id: crypto.randomUUID(),
      name: name.trim(),
      color,
      userId: userId ?? null,
      ...(profile?.email !== undefined && { email: profile.email }),
      ...(profile?.phone !== undefined && { phone: profile.phone }),
      role: profile?.role !== undefined ? profile.role : "member",
      ...(profile?.notes !== undefined && { notes: profile.notes }),
      ...(profile?.subjectIds !== undefined && { subjectIds: profile.subjectIds }),
    };

    data.teachers.push(newTeacher);
    data.lastModified = new Date().toISOString();

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 강사 추가 성공", {
        teacherId: newTeacher.id,
        name: newTeacher.name,
      });
      return { success: true, data: newTeacher };
    }
    return { success: false, error: "localStorage 저장 실패" };
  } catch (error) {
    logger.error("localStorageCrud - 강사 추가 실패:", undefined, error as Error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "강사 추가 실패",
    };
  }
};

/**
 * 강사 수정
 */
export const updateTeacherInLocal = (
  id: string,
  updates: {
    name?: string;
    color?: string;
    userId?: string | null;
    email?: string | null;
    phone?: string | null;
    role?: TeacherRole | null;
    notes?: string | null;
    subjectIds?: string[];
  }
): CrudResult<Teacher> => {
  try {
    const data = getClassPlannerData();
    const teacherIndex = data.teachers.findIndex((t) => t.id === id);

    if (teacherIndex === -1) {
      return { success: false, error: "강사를 찾을 수 없습니다." };
    }

    // 중복 검사 — 변경 후 식별 조합이 다른 강사와 충돌하는지 (UAT 2026-05-10).
    {
      const current = data.teachers[teacherIndex];
      const normT = (v: string | null | undefined) => (v ?? "").trim();
      const merged = {
        name: updates.name !== undefined ? updates.name : current.name,
        email: updates.email !== undefined ? updates.email : current.email,
        phone: updates.phone !== undefined ? updates.phone : current.phone,
      };
      const isDuplicate = data.teachers.some(
        (t, index) =>
          index !== teacherIndex &&
          normT(t.name).toLowerCase() === normT(merged.name).toLowerCase() &&
          normT(t.email) === normT(merged.email) &&
          normT(t.phone) === normT(merged.phone),
      );
      if (isDuplicate) {
        return {
          success: false,
          error: "이미 동일한 강사(이름·이메일·전화 일치)가 존재합니다.",
        };
      }
    }

    const updatedTeacher: Teacher = {
      ...data.teachers[teacherIndex],
      ...(updates.name !== undefined && { name: updates.name.trim() }),
      ...(updates.color !== undefined && { color: updates.color }),
      ...(updates.userId !== undefined && { userId: updates.userId }),
      ...(updates.email !== undefined && { email: updates.email }),
      ...(updates.phone !== undefined && { phone: updates.phone }),
      ...(updates.role !== undefined && { role: updates.role }),
      ...(updates.notes !== undefined && { notes: updates.notes }),
      ...(updates.subjectIds !== undefined && { subjectIds: updates.subjectIds }),
    };

    data.teachers[teacherIndex] = updatedTeacher;
    data.lastModified = new Date().toISOString();

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 강사 수정 성공", { teacherId: id, updates });
      return { success: true, data: updatedTeacher };
    }
    return { success: false, error: "localStorage 저장 실패" };
  } catch (error) {
    logger.error("localStorageCrud - 강사 수정 실패:", undefined, error as Error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "강사 수정 실패",
    };
  }
};

/**
 * 강사 삭제 (관련 sessions의 teacherId를 null로 설정)
 */
export const deleteTeacherFromLocal = (id: string): CrudResult<boolean> => {
  try {
    const data = getClassPlannerData();
    const teacherIndex = data.teachers.findIndex((t) => t.id === id);

    if (teacherIndex === -1) {
      return { success: false, error: "강사를 찾을 수 없습니다." };
    }

    const deletedTeacher = data.teachers[teacherIndex];
    data.teachers.splice(teacherIndex, 1);

    // 해당 강사가 배정된 세션의 teacherId를 null로 초기화
    data.sessions = data.sessions.map((session) => {
      if (session.teacherId === id) {
        return { ...session, teacherId: undefined };
      }
      return session;
    });

    data.lastModified = new Date().toISOString();

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 강사 삭제 성공", {
        teacherId: id,
        name: deletedTeacher.name,
      });
      return { success: true, data: true };
    }
    return { success: false, error: "localStorage 저장 실패" };
  } catch (error) {
    logger.error("localStorageCrud - 강사 삭제 실패:", undefined, error as Error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "강사 삭제 실패",
    };
  }
};

/**
 * 강사 조회
 */
export const getTeacherFromLocal = (id: string): Teacher | null => {
  try {
    const data = getClassPlannerData();
    return data.teachers.find((t) => t.id === id) || null;
  } catch (error) {
    logger.error("localStorageCrud - 강사 조회 실패:", undefined, error as Error);
    return null;
  }
};

/**
 * 모든 강사 조회
 */
export const getAllTeachersFromLocal = (): Teacher[] => {
  try {
    const data = getClassPlannerData();
    return data.teachers;
  } catch (error) {
    logger.error("localStorageCrud - 강사 목록 조회 실패:", undefined, error as Error);
    return [];
  }
};

export const addTeacherSubjectToLocal = (
  teacherId: string,
  subjectId: string
): CrudResult<boolean> => {
  try {
    const data = getClassPlannerData();
    const teacher = data.teachers.find((t) => t.id === teacherId);
    if (!teacher) return { success: false, error: "강사를 찾을 수 없습니다." };
    if (!teacher.subjectIds) teacher.subjectIds = [];
    if (!teacher.subjectIds.includes(subjectId)) {
      teacher.subjectIds.push(subjectId);
      data.lastModified = new Date().toISOString();
      if (!setClassPlannerData(data)) return { success: false, error: "localStorage 저장 실패" };
    }
    return { success: true, data: true };
  } catch (error) {
    logger.error("localStorageCrud - 강사-과목 추가 실패:", undefined, error as Error);
    return { success: false, error: error instanceof Error ? error.message : "강사-과목 추가 실패" };
  }
};

export const removeTeacherSubjectFromLocal = (
  teacherId: string,
  subjectId: string
): CrudResult<boolean> => {
  try {
    const data = getClassPlannerData();
    const teacher = data.teachers.find((t) => t.id === teacherId);
    if (!teacher) return { success: false, error: "강사를 찾을 수 없습니다." };
    teacher.subjectIds = (teacher.subjectIds ?? []).filter((id) => id !== subjectId);
    data.lastModified = new Date().toISOString();
    if (!setClassPlannerData(data)) return { success: false, error: "localStorage 저장 실패" };
    return { success: true, data: true };
  } catch (error) {
    logger.error("localStorageCrud - 강사-과목 삭제 실패:", undefined, error as Error);
    return { success: false, error: error instanceof Error ? error.message : "강사-과목 삭제 실패" };
  }
};

// ===== Sessions CRUD =====

/**
 * 세션 추가
 */
export const addSessionToLocal = (
  sessionData: Omit<Session, "id">
): CrudResult<Session> => {
  try {
    const data = getClassPlannerData();

    // 새 세션 생성
    const newSession: Session = {
      id: crypto.randomUUID(),
      ...sessionData,
    };

    // 데이터 업데이트
    data.sessions.push(newSession);
    data.lastModified = new Date().toISOString(); // CRU 작업 시 lastModified 갱신

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 세션 추가 성공", {
        sessionId: newSession.id,
        weekday: newSession.weekday,
        time: `${newSession.startsAt}-${newSession.endsAt}`,
      });

      return {
        success: true,
        data: newSession,
      };
    } else {
      return {
        success: false,
        error: "localStorage 저장 실패",
      };
    }
  } catch (error) {
    logger.error(
      "localStorageCrud - 세션 추가 실패:",
      undefined,
      error as Error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "세션 추가 실패",
    };
  }
};

/**
 * 세션 수정
 */
export const updateSessionInLocal = (
  id: string,
  updates: Partial<Omit<Session, "id">>
): CrudResult<Session> => {
  try {
    const data = getClassPlannerData();
    const sessionIndex = data.sessions.findIndex((s) => s.id === id);

    if (sessionIndex === -1) {
      return {
        success: false,
        error: "세션을 찾을 수 없습니다.",
      };
    }

    // 세션 업데이트
    const updatedSession: Session = {
      ...data.sessions[sessionIndex],
      ...updates,
    };

    data.sessions[sessionIndex] = updatedSession;
    data.lastModified = new Date().toISOString(); // CRU 작업 시 lastModified 갱신

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 세션 수정 성공", {
        sessionId: id,
        updates,
      });

      return {
        success: true,
        data: updatedSession,
      };
    } else {
      return {
        success: false,
        error: "localStorage 저장 실패",
      };
    }
  } catch (error) {
    logger.error(
      "localStorageCrud - 세션 수정 실패:",
      undefined,
      error as Error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "세션 수정 실패",
    };
  }
};

/**
 * 세션 삭제
 */
export const deleteSessionFromLocal = (id: string): CrudResult<boolean> => {
  try {
    const data = getClassPlannerData();
    const sessionIndex = data.sessions.findIndex((s) => s.id === id);

    if (sessionIndex === -1) {
      return {
        success: false,
        error: "세션을 찾을 수 없습니다.",
      };
    }

    const deletedSession = data.sessions[sessionIndex];
    data.sessions.splice(sessionIndex, 1);
    data.lastModified = new Date().toISOString(); // CRU 작업 시 lastModified 갱신

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 세션 삭제 성공", {
        sessionId: id,
        weekday: deletedSession.weekday,
        time: `${deletedSession.startsAt}-${deletedSession.endsAt}`,
      });

      return {
        success: true,
        data: true,
      };
    } else {
      return {
        success: false,
        error: "localStorage 저장 실패",
      };
    }
  } catch (error) {
    logger.error(
      "localStorageCrud - 세션 삭제 실패:",
      undefined,
      error as Error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "세션 삭제 실패",
    };
  }
};

// ===== Enrollments CRUD =====

/**
 * 등록 추가
 */
export const addEnrollmentToLocal = (
  studentId: string,
  subjectId: string
): CrudResult<Enrollment> => {
  try {
    const data = getClassPlannerData();

    // 중복 등록 검사
    const isDuplicate = data.enrollments.some(
      (e) => e.studentId === studentId && e.subjectId === subjectId
    );
    if (isDuplicate) {
      return {
        success: false,
        error: "이미 해당 과목에 등록된 학생입니다.",
      };
    }

    // 새 등록 생성
    const newEnrollment: Enrollment = {
      id: crypto.randomUUID(),
      studentId,
      subjectId,
    };

    // 데이터 업데이트
    data.enrollments.push(newEnrollment);
    data.lastModified = new Date().toISOString(); // CRU 작업 시 lastModified 갱신

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 등록 추가 성공", {
        enrollmentId: newEnrollment.id,
        studentId,
        subjectId,
      });

      return {
        success: true,
        data: newEnrollment,
      };
    } else {
      return {
        success: false,
        error: "localStorage 저장 실패",
      };
    }
  } catch (error) {
    logger.error(
      "localStorageCrud - 등록 추가 실패:",
      undefined,
      error as Error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "등록 추가 실패",
    };
  }
};

/**
 * 등록 삭제
 */
export const deleteEnrollmentFromLocal = (id: string): CrudResult<boolean> => {
  try {
    const data = getClassPlannerData();
    const enrollmentIndex = data.enrollments.findIndex((e) => e.id === id);

    if (enrollmentIndex === -1) {
      return {
        success: false,
        error: "등록을 찾을 수 없습니다.",
      };
    }

    const deletedEnrollment = data.enrollments[enrollmentIndex];
    data.enrollments.splice(enrollmentIndex, 1);

    // 해당 enrollment를 사용하는 session들에서 enrollmentId 제거
    data.sessions = data.sessions
      .map((session) => {
        if (session.enrollmentIds?.includes(id)) {
          return {
            ...session,
            enrollmentIds: session.enrollmentIds.filter((eId) => eId !== id),
          };
        }
        return session;
      })
      .filter(
        (session) =>
          // enrollmentIds가 빈 배열이 된 session은 삭제
          session.enrollmentIds && session.enrollmentIds.length > 0
      );

    data.lastModified = new Date().toISOString(); // CRU 작업 시 lastModified 갱신

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - 등록 삭제 성공", {
        enrollmentId: id,
        studentId: deletedEnrollment.studentId,
        subjectId: deletedEnrollment.subjectId,
      });

      return {
        success: true,
        data: true,
      };
    } else {
      return {
        success: false,
        error: "localStorage 저장 실패",
      };
    }
  } catch (error) {
    logger.error(
      "localStorageCrud - 등록 삭제 실패:",
      undefined,
      error as Error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "등록 삭제 실패",
    };
  }
};

/**
 * Enrollment id 교체 — server idempotent create가 *기존 row의 id*를 반환했을 때
 * localStorage 측 (`enrollments[].id` + 모든 `sessions[].enrollmentIds`) 를 한 번에 갱신.
 *
 * Why: 클라이언트 localStorage의 enrollment id 가 server enrollment id 와 다른 케이스
 * (anonymous → 로그인, 머신 간 sync 등에서 발생). reconcile 안 하면 후속 session POST
 * 가 잘못된 enrollmentIds 로 가서 session_enrollments FK 위반 → 500 → outbox 무한 retry.
 *
 * 멱등: oldId === newId 또는 oldId가 enrollments에 없으면 no-op + true.
 */
export const replaceEnrollmentId = (oldId: string, newId: string): boolean => {
  try {
    if (oldId === newId) return true;
    const data = getClassPlannerData();
    const target = data.enrollments.find((e) => e.id === oldId);
    if (!target) return true; // 이미 정리됨 — 멱등

    // 새 id가 이미 enrollments에 있으면 (예: 같은 (student, subject) 로 별도 row 존재)
    // oldId entry 제거 + sessions[].enrollmentIds 만 newId로 교체 (중복 제거).
    const newAlreadyPresent = data.enrollments.some((e) => e.id === newId);

    if (newAlreadyPresent) {
      data.enrollments = data.enrollments.filter((e) => e.id !== oldId);
    } else {
      data.enrollments = data.enrollments.map((e) =>
        e.id === oldId ? { ...e, id: newId } : e
      );
    }

    data.sessions = data.sessions.map((session) => {
      if (!session.enrollmentIds || !session.enrollmentIds.includes(oldId)) {
        return session;
      }
      const replaced = session.enrollmentIds.map((eId) =>
        eId === oldId ? newId : eId
      );
      // 중복 제거 (newId가 이미 배열에 있으면 oldId→newId 변환 후 dedupe)
      const deduped = Array.from(new Set(replaced));
      return { ...session, enrollmentIds: deduped };
    });

    data.lastModified = new Date().toISOString();

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - enrollment id 교체 성공", {
        oldId,
        newId,
        newAlreadyPresent,
      });
      return true;
    }
    return false;
  } catch (error) {
    logger.error(
      "localStorageCrud - enrollment id 교체 실패:",
      undefined,
      error as Error
    );
    return false;
  }
};

/**
 * Student id 교체 — server idempotent create가 *기존 row의 id*를 반환했을 때
 * localStorage 측 (`students[].id` + 모든 `enrollments[].studentId`) 를 갱신.
 *
 * 멱등: oldId === newId 또는 oldId가 students에 없으면 no-op + true.
 */
export const replaceStudentId = (oldId: string, newId: string): boolean => {
  try {
    if (oldId === newId) return true;
    const data = getClassPlannerData();
    const target = data.students.find((s) => s.id === oldId);
    if (!target) return true;

    const newAlreadyPresent = data.students.some((s) => s.id === newId);
    if (newAlreadyPresent) {
      data.students = data.students.filter((s) => s.id !== oldId);
    } else {
      data.students = data.students.map((s) =>
        s.id === oldId ? { ...s, id: newId } : s
      );
    }

    data.enrollments = data.enrollments.map((e) =>
      e.studentId === oldId ? { ...e, studentId: newId } : e
    );

    data.lastModified = new Date().toISOString();

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - student id 교체 성공", {
        oldId,
        newId,
        newAlreadyPresent,
      });
      return true;
    }
    return false;
  } catch (error) {
    logger.error("localStorageCrud - student id 교체 실패:", undefined, error as Error);
    return false;
  }
};

/**
 * Subject id 교체 — server idempotent create가 *기존 row의 id*를 반환했을 때
 * localStorage 측 (`subjects[].id` + 모든 `enrollments[].subjectId`) 를 갱신.
 *
 * 멱등: oldId === newId 또는 oldId가 subjects에 없으면 no-op + true.
 */
export const replaceSubjectId = (oldId: string, newId: string): boolean => {
  try {
    if (oldId === newId) return true;
    const data = getClassPlannerData();
    const target = data.subjects.find((s) => s.id === oldId);
    if (!target) return true;

    const newAlreadyPresent = data.subjects.some((s) => s.id === newId);
    if (newAlreadyPresent) {
      data.subjects = data.subjects.filter((s) => s.id !== oldId);
    } else {
      data.subjects = data.subjects.map((s) =>
        s.id === oldId ? { ...s, id: newId } : s
      );
    }

    data.enrollments = data.enrollments.map((e) =>
      e.subjectId === oldId ? { ...e, subjectId: newId } : e
    );

    data.lastModified = new Date().toISOString();

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - subject id 교체 성공", {
        oldId,
        newId,
        newAlreadyPresent,
      });
      return true;
    }
    return false;
  } catch (error) {
    logger.error("localStorageCrud - subject id 교체 실패:", undefined, error as Error);
    return false;
  }
};

/**
 * Teacher id 교체 — server idempotent create가 *기존 row의 id*를 반환했을 때
 * localStorage 측 (`teachers[].id` + 모든 `sessions[].teacherId`) 를 갱신.
 *
 * 멱등: oldId === newId 또는 oldId가 teachers에 없으면 no-op + true.
 */
export const replaceTeacherId = (oldId: string, newId: string): boolean => {
  try {
    if (oldId === newId) return true;
    const data = getClassPlannerData();
    const target = data.teachers.find((t) => t.id === oldId);
    if (!target) return true;

    const newAlreadyPresent = data.teachers.some((t) => t.id === newId);
    if (newAlreadyPresent) {
      data.teachers = data.teachers.filter((t) => t.id !== oldId);
    } else {
      data.teachers = data.teachers.map((t) =>
        t.id === oldId ? { ...t, id: newId } : t
      );
    }

    data.sessions = data.sessions.map((session) =>
      session.teacherId === oldId ? { ...session, teacherId: newId } : session
    );

    data.lastModified = new Date().toISOString();

    if (setClassPlannerData(data)) {
      logger.info("localStorageCrud - teacher id 교체 성공", {
        oldId,
        newId,
        newAlreadyPresent,
      });
      return true;
    }
    return false;
  } catch (error) {
    logger.error("localStorageCrud - teacher id 교체 실패:", undefined, error as Error);
    return false;
  }
};

// ===== 사용자별 데이터 삭제 =====

export const clearUserClassPlannerData = (userId: string): boolean => {
  try {
    if (typeof window === "undefined") return false;
    const legacyKey = `classPlannerData:${userId}`;
    localStorage.removeItem(legacyKey);
    dataCache.delete(legacyKey);
    logger.info("localStorageCrud - 사용자 데이터 삭제 완료", { userId });
    return true;
  } catch (error) {
    logger.error("localStorageCrud - 사용자 데이터 삭제 실패:", undefined, error as Error);
    return false;
  }
};

// ===== 통합 업데이트 함수 =====

/**
 * 전체 데이터 업데이트 (Schedule 페이지용)
 */
export const updateClassPlannerData = (
  updates: Partial<ClassPlannerData>
): CrudResult<ClassPlannerData> => {
  try {
    const currentData = getClassPlannerData();

    const updatedData: ClassPlannerData = {
      ...currentData,
      ...updates,
      lastModified: new Date().toISOString(), // CRU 작업 시 lastModified 갱신
    };

    if (setClassPlannerData(updatedData)) {
      logger.info("localStorageCrud - 전체 데이터 업데이트 성공", {
        studentCount: updatedData.students.length,
        subjectCount: updatedData.subjects.length,
        sessionCount: updatedData.sessions.length,
        enrollmentCount: updatedData.enrollments.length,
      });

      return {
        success: true,
        data: updatedData,
      };
    } else {
      return {
        success: false,
        error: "localStorage 저장 실패",
      };
    }
  } catch (error) {
    logger.error(
      "localStorageCrud - 전체 데이터 업데이트 실패:",
      undefined,
      error as Error
    );
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "전체 데이터 업데이트 실패",
    };
  }
};
